/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useStoreDispatch } from "../../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../../store/helpers/helperReducers";
import { IThresholdCrossingSettingAction } from "../../../../store/api/admin/types";
import { useThresholdCrossingSettingActionData } from "./hooks";
import { isError } from "../../../../utils/validations";
import AutocompleteSelect from "../../../Shared/Dropdown/AutocompleteSelect";
import { Link } from "react-router-dom";
import { getNewOptions } from "../../../../components/Tasks/helpers";
import { IOption } from "../../../Shared/Filter/types";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { RadixSelectControl } from "../../../ui/radix-select";

interface IAddEditThresholdCrossingSettingActionProps {
  selectedRow: IThresholdCrossingSettingAction;
  modelOpenMode?: string;
}

const AddEditProfileAction: React.FC<IAddEditThresholdCrossingSettingActionProps> = ({ modelOpenMode = "Add", selectedRow }) => {
  const dispatch = useStoreDispatch();
  const {
    thresholdCrossingSettingActionData,
    setThresholdCrossingSettingActionData,
    submitHandler,
    onBlurHandler,
    errMsg,
    setErrMsg,
    emailGroupNameOptions = [],
    snmpGroupNameOptions = [],
    emailGroupName,
    setEmailGroupName,
    snmpGroupName,
    setSnmpGroupName,
    actionType,
    details,
    errorsPresent,
  } = useThresholdCrossingSettingActionData(selectedRow);

  const [showActionDetailsMode, setShowActionDetailsMode] = useState("");
  const actionList = [
    { text: "EMAIL", value: "EMAIL" },
  ];

  const isDirty = (
    thresholdCrossingSettingActionData.actionType !== selectedRow.actionType ||
    JSON.stringify(thresholdCrossingSettingActionData.details) !== JSON.stringify(selectedRow.details)
  );

  const validationPassed = 
    !!thresholdCrossingSettingActionData.actionType && 
    !!thresholdCrossingSettingActionData.details.length;

  const isDiabledSave = 
    isError(errMsg) ||
    (modelOpenMode === "Edit" && !isDirty) ||
    (modelOpenMode !== "Edit" && !validationPassed);

  useEffect(() => {
    if (thresholdCrossingSettingActionData?.actionType) {
      setShowActionDetailsMode(thresholdCrossingSettingActionData.actionType);
    }
  }, [thresholdCrossingSettingActionData.actionType]);

  useEffect(() => {
    if (thresholdCrossingSettingActionData.details.length >= 0) {
      if (thresholdCrossingSettingActionData.actionType == "EMAIL") {
        let emailGroupList = thresholdCrossingSettingActionData.details;
        setEmailGroupName(
          emailGroupList.map((emailGroup: string) => ({
            title: emailGroup,
            value: emailGroup,
          }))
        );
      } else {
        let snmpNotificationGroupList = thresholdCrossingSettingActionData.details;
        setSnmpGroupName(
          snmpNotificationGroupList.map((snmpGroup: string) => ({
            title: snmpGroup,
            value: snmpGroup,
          }))
        );
      }
    }
  }, [thresholdCrossingSettingActionData.details]);

  const autocompleteEmailGroupSelectHandler = (event: any, newValue: IOption[] | null) => {
    setEmailGroupName(newValue || []);
    setThresholdCrossingSettingActionData({ ...thresholdCrossingSettingActionData, ["details"]: newValue?.map((item) => item.value) || [] });
    setErrMsg({ ...errMsg, ["details"]: "" });
  };

  const autocompleteSnmpGroupSelectHandler = (event: any, newValue: IOption[] | null) => {
    setSnmpGroupName(newValue || []);
    setThresholdCrossingSettingActionData({ ...thresholdCrossingSettingActionData, ["details"]: newValue?.map((item) => item.value) || [] });
    setErrMsg({ ...errMsg, ["details"]: "" });
  };

  const handleChangeAction = (name: string, value: string) => {
    if (value === "EMAIL" || value === "SNMP") {
      setShowActionDetailsMode(value);
    }
    setThresholdCrossingSettingActionData({ ...thresholdCrossingSettingActionData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button onClick={submitHandler} disabled={isDiabledSave || errorsPresent}>
        Save
      </Button>
    </>
  );

  const noOption = (
    <Typography>
      {`No email group available. `}
      <Link className="nooption-link" to={"/administration/email"}>
        "Click here"
      </Link>
      {` to add an email group.`}
    </Typography>
  );

  const noOptionSnmp = (
    <Typography>
      {`No snmp group available. `}
      <Link className="nooption-link" to={"/administration/northbound-interface"}>
        "Click here"
      </Link>
      {` to add an snmp group.`}
    </Typography>
  );

  return (
    <Sheet
      open={true}
      onClose={handleClose}
      title={`${modelOpenMode} ${modelOpenMode?.toLowerCase() !== "add" ? "action" : "action"}`}
      footer={footer}
      width="520px"
    >
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Select Action */}
          <div className="space-y-2">
            <Label htmlFor="action-type">Select Action</Label>
            <RadixSelectControl
              options={[{ label: "Select action", value: "placeholder" }, ...actionList.map(item => ({ label: item.text, value: item.value }))]}
              value={thresholdCrossingSettingActionData?.actionType || "placeholder"}
              onValueChange={(val) => handleChangeAction(actionType, val === "placeholder" ? "" : val)}
              autoFocus={true}
            />
          </div>

          {/* Email Group Name */}
          {showActionDetailsMode === "EMAIL" && (
            <div className="space-y-2">
              <Label>Email Group Name</Label>
              <AutocompleteSelect
                name="{details}"
                value={emailGroupName}
                multiple={true}
                id="emailGroupName"
                options={getNewOptions(emailGroupNameOptions, emailGroupName)}
                label={""}
                margin={"normal"}
                size={"small"}
                noOptionsText={noOption}
                onChange={autocompleteEmailGroupSelectHandler}
                disablePortal
              />
            </div>
          )}

          {/* SNMP Group Name */}
          {showActionDetailsMode === "SNMP" && (
            <div className="space-y-2">
              <Label>Snmp Group Name</Label>
              <AutocompleteSelect
                name="{details}"
                value={snmpGroupName}
                multiple={true}
                id="snmpGroupName"
                options={getNewOptions(snmpGroupNameOptions, snmpGroupName)}
                label={""}
                margin={"normal"}
                size={"small"}
                noOptionsText={noOptionSnmp}
                onChange={autocompleteSnmpGroupSelectHandler}
                disablePortal
              />
            </div>
          )}
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditProfileAction;
